import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { movies, heroFeatured, getHotMovies, getByCategory } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';

function HeroSection() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % heroFeatured.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const item = heroFeatured[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background-50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full h-full flex items-end pb-12 md:pb-16">
        <div className="w-full px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-start gap-6 max-w-5xl mx-auto w-full">
            {/* Poster */}
            <div className="hidden md:block w-40 md:w-48 flex-shrink-0 rounded-lg overflow-hidden">
              <img
                src={item.image}
                alt={item.title}
                className="w-full aspect-[3/4] object-cover"
              />
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 text-xs rounded-full bg-primary-500/90 text-background-50 dark:text-foreground-950 whitespace-nowrap">
                  {item.categoryLabel}
                </span>
                {item.isNew && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-accent-500/90 text-background-950 whitespace-nowrap">
                    NEW
                  </span>
                )}
              </div>
              <h2 className="text-2xl md:text-4xl font-heading text-foreground-50 font-bold mb-3">
                {item.title}
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-300 mb-3">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  {item.rating}
                </span>
                <span>{item.year}</span>
                <span>{item.region}</span>
                <span>{item.genre.join(' / ')}</span>
              </div>
              <p className="text-sm text-foreground-400 leading-relaxed line-clamp-2 mb-4 max-w-xl hidden sm:block">
                {item.description}
              </p>
              <Link
                to={`/article/${item.id}`}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-fill"></i>
                立即观看
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Dots */}
      <div className="absolute bottom-4 right-4 md:right-6 z-10 flex items-center gap-1.5">
        {heroFeatured.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
              i === current ? 'bg-primary-500 w-5' : 'bg-foreground-400/50 hover:bg-foreground-300'
            }`}
            aria-label={`切换到第${i + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}

function SectionHeader({ title, moreLink }: { title: string; moreLink?: string }) {
  return (
    <div className="flex items-center justify-between mb-5 md:mb-4">
      <h3 className="text-lg md:text-xl font-heading text-foreground-50 font-bold flex items-center gap-2">
        <span className="w-1 h-5 bg-primary-500 rounded-full inline-block"></span>
        {title}
      </h3>
      {moreLink && (
        <Link
          to={moreLink}
          className="text-sm text-foreground-500 hover:text-primary-400 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
        >
          查看更多 <i className="ri-arrow-right-s-line"></i>
        </Link>
      )}
    </div>
  );
}

function MovieCard({ item }: { item: MovieItem }) {
  return (
    <Link
      to={`/article/${item.id}`}
      className="group block cursor-pointer"
      data-product-shop
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-2.5 bg-background-200">
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover transition-transform duration-400 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          <span className="px-1.5 py-0.5 text-[10px] rounded bg-primary-500/90 text-background-50 dark:text-foreground-950 whitespace-nowrap">
            {item.rating}
          </span>
        </div>
        {item.isNew && (
          <div className="absolute top-2 right-2">
            <span className="px-1.5 py-0.5 text-[10px] rounded bg-accent-500/90 text-background-950 whitespace-nowrap">
              NEW
            </span>
          </div>
        )}
        <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="text-xs text-foreground-50 bg-primary-500/90 px-3 py-1.5 rounded-full flex items-center justify-center gap-1 whitespace-nowrap">
            <i className="ri-play-fill text-xs"></i> 立即观看
          </span>
        </div>
      </div>
      <h4 className="text-sm font-medium text-foreground-50 line-clamp-1 group-hover:text-primary-400 transition-colors">
        {item.title}
      </h4>
      <p className="text-xs text-foreground-500 mt-0.5">{item.updateInfo}</p>
    </Link>
  );
}

function CategoryRow({ title, items, moreLink }: { title: string; items: MovieItem[]; moreLink: string }) {
  return (
    <section className="w-full px-4 md:px-6 py-8 md:py-10">
      <div className="max-w-6xl mx-auto">
        <SectionHeader title={title} moreLink={moreLink} />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
          {items.map((item) => (
            <MovieCard key={item.id} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryNav() {
  const categories = [
    { label: '电影', icon: 'ri-film-line', path: '/category/movie', desc: '热门大片' },
    { label: '电视剧', icon: 'ri-tv-line', path: '/category/tv', desc: '高分剧集' },
    { label: '综艺', icon: 'ri-live-line', path: '/category/variety', desc: '爆笑综艺' },
    { label: '动漫', icon: 'ri-rocket-line', path: '/category/anime', desc: '热血番剧' },
  ];

  return (
    <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100">
      <div className="max-w-6xl mx-auto">
        <SectionHeader title="全部分类" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-5">
          {categories.map((cat) => (
            <Link
              key={cat.path}
              to={cat.path}
              className="flex items-center gap-3 p-4 rounded-lg bg-background-50 hover:bg-background-200/70 border border-background-200/50 hover:border-primary-500/30 transition-all group cursor-pointer"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 group-hover:bg-primary-500 group-hover:text-background-50 dark:group-hover:text-foreground-950 transition-all">
                <i className={`${cat.icon} text-lg md:text-xl`}></i>
              </div>
              <div>
                <span className="text-sm font-medium text-foreground-50 block">{cat.label}</span>
                <span className="text-xs text-foreground-500">{cat.desc}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsBanner() {
  const stats = [
    { value: '100,000+', label: '影视资源' },
    { value: '99.9%', label: '播放成功率' },
    { value: '50M+', label: '月活用户' },
    { value: '4.8', label: '用户评分' },
  ];

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/50">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, i) => (
            <div key={i} className="text-center">
              <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-1">
                {stat.value}
              </div>
              <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  const features = [
    {
      icon: 'ri-flashlight-line',
      title: '秒速加载',
      desc: '采用全球CDN加速，视频秒开不缓冲，极致流畅体验',
    },
    {
      icon: 'ri-hd-line',
      title: '高清画质',
      desc: '支持1080P超清画质，部分影片支持4K，带来影院级视觉享受',
    },
    {
      icon: 'ri-smartphone-line',
      title: '多端适配',
      desc: '电脑、平板、手机全平台完美适配，随时随地轻松追剧',
    },
    {
      icon: 'ri-shield-check-line',
      title: '完全免费',
      desc: '所有影片无需付费，无需会员，无广告打扰，纯粹观影体验',
    },
  ];

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14">
      <div className="max-w-6xl mx-auto">
        <SectionHeader title="为什么选择八戒影视" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
          {features.map((f, i) => (
            <div
              key={i}
              className="p-4 md:p-5 rounded-lg bg-background-100 border border-background-200/50 hover:border-primary-500/20 transition-all group cursor-default"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/10 text-accent-400 mb-3 group-hover:bg-accent-500 group-hover:text-background-50 dark:group-hover:text-foreground-950 transition-all">
                <i className={`${f.icon} text-lg`}></i>
              </div>
              <h4 className="text-sm font-semibold text-foreground-50 mb-1.5">{f.title}</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const hotMovies = getHotMovies().slice(0, 12);
  const newMovies = getByCategory('movie').filter(m => m.isNew).slice(0, 6);
  const latestTV = getByCategory('tv').slice(0, 6);
  const varietyShows = getByCategory('variety').slice(0, 6);
  const animeList = getByCategory('anime').slice(0, 6);

  // JSON-LD structured data
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '八戒影视网',
    url: 'https://www.bajieying.com/',
    description: '八戒影视网提供最新高清电影、热播电视剧、热门综艺和动漫免费在线观看，无需付费即可畅享全集完整版，播放流畅不卡顿，秒速加载不缓冲。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.bajieying.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <HeroSection />
      <CategoryNav />
      <CategoryRow title="热门推荐" items={hotMovies} moreLink="/category/movie" />
      <StatsBanner />
      <CategoryRow title="最新电影" items={newMovies} moreLink="/category/movie" />
      <CategoryRow title="热播电视剧" items={latestTV} moreLink="/category/tv" />
      <CategoryRow title="热门综艺" items={varietyShows} moreLink="/category/variety" />
      <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <SectionHeader title="热门动漫" moreLink="/category/anime" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {animeList.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>
      <Features />
    </>
  );
}